import type { StripeError } from '@stripe/stripe-js';

/**
 * Check if a Stripe payment error indicates that the payment already succeeded.
 *
 * This can happen with cards that don't require 3DS authentication - Stripe automatically
 * confirms the PaymentIntent when it's created. When we try to manually confirm it again,
 * Stripe returns an error even though the payment succeeded.
 *
 * @param error - The error returned from stripe.confirmPayment()
 * @returns true if the payment already succeeded and we should proceed, false otherwise
 *
 * @example
 * const { error } = await stripe.confirmPayment({ ... });
 * if (error) {
 *   if (isPaymentAlreadySucceeded(error)) {
 *     // Payment succeeded - proceed to success page
 *     router.push('/success');
 *   } else {
 *     // Real error - show error message
 *     toast.error(error.message);
 *   }
 * }
 */
export function isPaymentAlreadySucceeded(error: StripeError): boolean {
    // Extract error details
    const errorCode = (error as any).code;
    const errorMessage = error.message?.toLowerCase() || '';
    const paymentIntentStatus = (error as any).payment_intent?.status;

    // Check if the error indicates payment already succeeded
    // Scenario 1: payment_intent_unexpected_state with succeeded status
    if (errorCode === 'payment_intent_unexpected_state' && paymentIntentStatus === 'succeeded') {
        console.error(
            'Payment already succeeded (status:',
            paymentIntentStatus,
            ', code:',
            errorCode,
            '), proceeding to success page'
        );
        return true;
    }

    // Scenario 2: Error message explicitly mentions "already succeeded"
    if (error.type === 'invalid_request_error' && errorMessage.includes('already succeeded')) {
        console.error('Payment already succeeded (message check), proceeding to success page');
        return true;
    }

    return false;
}
